import { DataResult, DataStream, WidgetSettingsType } from '@thingslog/repositories';
import { DataResultParseStrategy } from './DataResultParseStrategy';
import {
  DataResultParsed,
  DataResultParseDataPoint,
  DataResultParseStream,
} from '../DataResultParsed';
import { createUnitTooltipFormatter, formatValueWithUnit } from '../metadata';
import { t } from 'i18next';

type SignalMetric = 'level' | 'rssi';

const signalMetricConfig: Record<
  SignalMetric,
  {
    color: string;
    dataIndex: number;
    label: string;
    unit: string;
  }
> = {
  level: {
    color: '#60a5fa',
    dataIndex: 0,
    label: 'Signal level',
    unit: '%',
  },
  rssi: {
    color: '#f97316',
    dataIndex: 1,
    label: 'RSSI',
    unit: 'dBm',
  },
};

const parseSelectedMetrics = (value?: string): SignalMetric[] => {
  const metrics = value
    ?.split(',')
    .filter((metric: string): metric is SignalMetric => metric === 'level' || metric === 'rssi');

  return metrics && metrics.length > 0 ? metrics : ['level'];
};

const resolveMetricValue = (stream: DataStream, metric: SignalMetric): number | null => {
  if (stream.type !== 'series') {
    return null;
  }

  const config = signalMetricConfig[metric];
  const value = stream.data[config.dataIndex] ?? stream.metaData[metric];
  const numericValue = typeof value === 'number' ? value : Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
};

export class ApiDevicesSignalDataResultParseStrategy implements DataResultParseStrategy {
  public parse(dataResult: DataResult, widgetType: WidgetSettingsType): DataResultParsed {
    const selectedMetrics = parseSelectedMetrics(dataResult.metaData.signalMetrics);
    const yAxisUnit =
      selectedMetrics.length === 1 ? signalMetricConfig[selectedMetrics[0]].unit : undefined;

    const parsed: DataResultParsed = {
      dataStreams: [],
      xAxis: {
        show: true,
        dataKey: widgetType === 'table' ? 'deviceLabel' : 'deviceNumber',
        name: t('devices', 'Devices'),
        formatter: (value: string) => value,
      },
      yAxis: {
        show: true,
        formatter: (value: number) => formatValueWithUnit(value, yAxisUnit),
      },
      legend: {
        show: true,
      },
      tooltip: {
        formatter: createUnitTooltipFormatter(),
        labelFormatter: (value: string | number, dataPoint?: DataResultParseDataPoint): string => {
          return dataPoint?.deviceName ? `${value} - ${dataPoint.deviceName}` : String(value);
        },
      },
    };

    const dataStream: DataResultParseStream = {
      data: [],
      visualization: selectedMetrics.map((metric: SignalMetric) => {
        const config = signalMetricConfig[metric];

        return {
          dataKey: metric,
          fill: config.color,
          innerRadius: metric === 'level' ? 70 : 48,
          outerRadius: metric === 'level' ? 90 : 68,
          name: metric === 'level' ? t('dashboard_signal_level', config.label) : config.label,
          nameKey: 'deviceLabel',
          unit: config.unit,
        };
      }),
    };

    dataResult.streams.forEach((stream: DataStream) => {
      if (stream.type !== 'series') return;
      const deviceNumber = stream.metaData.deviceNumber ?? '';
      const metricValues = selectedMetrics.reduce(
        (
          values: Partial<Record<SignalMetric, number>>,
          metric: SignalMetric
        ): Partial<Record<SignalMetric, number>> => {
          const value = resolveMetricValue(stream, metric);

          if (value !== null) {
            values[metric] = value;
          }

          return values;
        },
        {}
      );

      if (Object.keys(metricValues).length === 0) {
        return;
      }

      dataStream.data.push({
        ...stream.metaData,
        deviceLabel: stream.metaData.deviceName
          ? `${deviceNumber} - ${stream.metaData.deviceName}`
          : deviceNumber,
        ...metricValues,
      });
    });

    parsed.dataStreams.push(dataStream);

    return parsed;
  }
}
